import React, { useMemo, useCallback, useState } from 'react'
import classnames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'

import Table from '@nocode-works/template/components/table/SimpleTable'
import Window from '@nocode-works/template/components/dialog/Window'
import Currency from 'components/account/Currency'
import WebsitePayment from 'components/website/Payment'
import Products from 'components/account/Products'
import TitleValueGrid from 'components/widgets/TitleValueGrid'
import WebsiteScreenshot from 'components/widgets/WebsiteScreenshot'
import ConfirmSubscriptionChangeWindow from 'components/account/ConfirmSubscriptionChangeWindow'
import QuotaSummary from 'components/widgets/QuotaSummary'
import QuotaWarning from 'components/widgets/QuotaWarning'

import usePaymentConfirmation from 'components/hooks/usePaymentConfirmation'

import routerActions from '@nocode-works/template/store/modules/router'
import websiteSelectors from '@nocode-works/template/store/selectors/website'
import stripeSelectors from 'store/selectors/stripe'
import stripeActions from 'store/modules/stripe'
import priceUtils from 'utils/price'
import subscriptionUtils from 'utils/subscription'

import icons from '@nocode-works/template/icons'

import {
  CURRENCIES,
} from 'settings'

const VisibleIcon = icons.visible
const InfoIcon = icons.info
const CloseIcon = icons.close
const CardIcon = icons.payments
const UpgradeIcon = icons.tendingUp
const PdfIcon = icons.pdf
const SettingsIcon = icons.settings
const AnalyticsIcon = icons.analytics

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  pricingToolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  pricingNote: {
    flexGrow: 0,
  },
  currencies: {
    textAlign: 'right',
    flexGrow: 1,
  },
  viewPlansButton: {
    width: '100%',
    textAlign: 'right',
    paddingRight: theme.spacing(2),
  },
  button: {
    textTransform: 'none',
    color: '#666'
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionButton: {
    marginTop: theme.spacing(2),
  },
  gridTitle: {
    textAlign: 'right',
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  smallText: {
    fontSize: '0.8em'
  }
}))

const WEBSITE_FIELDS = [{
  title: 'Screenshot',
  name: 'screenshot',
}, {
  title: 'Name',
  name: 'name',
}, {
  title: 'Plan',
  name: 'plan',
}, {
  title: 'Quota Used',
  name: 'quota',
}]

const INVOICE_FIELDS = [{
  title: 'Date',
  name: 'date',
}, {
  title: 'Amount',
  name: 'amount',
}]

const CANCALLED_SUBSCRIPTION_FIELDS = [{
  title: 'Ended',
  name: 'ended',
}, {
  title: 'Monthly Total',
  name: 'total',
}]

const AccountPaymentsNocode = ({

}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  usePaymentConfirmation()
  
  const [ viewPlans, setViewPlans ] = useState(false)

  const websiteList = useSelector(websiteSelectors.ownedWebsiteList)
  const subscription = useSelector(stripeSelectors.subscription)
  const card = useSelector(stripeSelectors.card)
  const invoices = useSelector(stripeSelectors.invoices)
  const cancelledSubscriptions = useSelector(stripeSelectors.cancelledSubscriptions)
  const changePlanWebsite = useSelector(stripeSelectors.changePlanWebsite)
  const changePlanInfoSummary = useSelector(stripeSelectors.changePlanInfoSummary)
  const cancelSubscriptionWindowOpen = useSelector(stripeSelectors.cancelSubscriptionWindowOpen)
  const reactivateSubscriptionWindowOpen = useSelector(stripeSelectors.reactivateSubscriptionWindowOpen)
  const cancelledSubscriptionInvoices = useSelector(stripeSelectors.cancelledSubscriptionInvoices)
  const viewCancelledSubscription = useSelector(stripeSelectors.viewCancelledSubscription)

  const onSetChangePlanWebsite = useCallback((value) => {
    dispatch(stripeActions.setChangePlanWebsite(value ? value.website : null))
  })

  const onViewSettings = useCallback((id) => {
    dispatch(routerActions.navigateTo('website.edit', {id}))
  })

  const onSetCancelSubscriptionWindowOpen = useCallback((value) => {
    dispatch(stripeActions.setCancelSubscriptionWindowOpen(value))
  })

  const onCancelSubscription = useCallback(() => {
    dispatch(stripeActions.cancelSubscription())
  })

  const onSetReactivateSubscriptionWindowOpen = useCallback((value) => {
    dispatch(stripeActions.setReactivateSubscriptionWindowOpen(value))
  })

  const onReactivateSubscription = useCallback(() => {
    dispatch(stripeActions.reactivateSubscription())
  })

  const onChangeCard = useCallback(() => {
    dispatch(stripeActions.changeCard())
  })

  const onSetViewCancelledSubscription = useCallback((sub) => {
    dispatch(stripeActions.setViewCancelledSubscription(sub))
    dispatch(stripeActions.setCancelledSubscriptionInvoices([]))
    if(sub) {
      dispatch(stripeActions.loadCancelledSubscriptionInvoices(sub.id))
    }
  })

  const websiteTableData = useMemo(() => {
    return websiteList.map(website => {
      return {
        id: website.id,
        website,
        name: <span className={ classes.smallText }>{ website.name }</span>,
        plan: (
          <span className={ classes.smallText }>
            {
              website.product ?
                website.product.metadata.nocodeId :
                'free'
            }
          </span>
        ),
        quota: <QuotaSummary
          website={ website }
        />,
        screenshot: (
          <a href={`/builder/website/${website.id}/`}>
            <WebsiteScreenshot
              image={ website.screenshot }
            />
          </a>
        ),
      }
    })
  }, [
    websiteList,
  ])

  const invoiceTableData = useMemo(() => {
    return invoices.map(invoice => {
      const currency = CURRENCIES.find(c => c.id == invoice.currency)
      return {
        id: invoice.id,
        date: new Date(invoice.created * 1000).toLocaleDateString(),
        amount: `${currency.symbol}${priceUtils.displayPrice(invoice.total)}`,
        invoice_pdf: invoice.invoice_pdf
      }
    })
  }, [
    invoices,
  ])

  const cancelledSubscriptionTableData = useMemo(() => {
    return cancelledSubscriptions.map(sub => {
      const info = subscriptionUtils.getSubscriptionInfo(sub)
      return {
        id: sub.id,
        sub,
        ended: info.periodEnd,
        total: `${ info.currency.symbol }${ priceUtils.displayPrice(info.total) }`,
      }
    })
  }, [
    cancelledSubscriptions,
  ])

  const cancelledSubscriptionInvoiceTableData = useMemo(() => {
    return cancelledSubscriptionInvoices.map(invoice => {
      const currency = CURRENCIES.find(c => c.id == invoice.currency)
      return {
        id: invoice.id,
        date: new Date(invoice.created * 1000).toLocaleDateString(),
        amount: `${currency.symbol}${priceUtils.displayPrice(invoice.total)}`,
        invoice_pdf: invoice.invoice_pdf
      }
    })
  }, [
    cancelledSubscriptionInvoices,
  ])

  const getWebsiteActions = useCallback((item) => {
    return ( 
      <>
        <Tooltip title={ `View Quota` }>
          <Button
            size="small"
            className={ classnames(classes.button, classes.marginRight) }
            variant="outlined"
            onClick={ () => dispatch(routerActions.navigateTo('website.edit', {
              id: item.id,
              section: 'plan'
            })) }
          >
            <AnalyticsIcon />
          </Button>
        </Tooltip>
        <Tooltip title={ `${ item.plan == 'free' ? 'Upgrade' : 'Change' } Plan` }>
          <Button
            size="small"
            className={ classnames(classes.button, classes.marginRight) }
            variant="outlined"
            onClick={ () => onSetChangePlanWebsite(item) }
          >
            <UpgradeIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Settings">
          <Button
              size="small"
              className={ classnames(classes.button) }
              variant="outlined"
              onClick={ () => onViewSettings(item.id) }
            >
              <SettingsIcon />
          </Button>
        </Tooltip>
      </>
    )
  }, [
    classes,
  ])

  const getInvoiceActions = useCallback((item) => {
    return (
      <>
        <Button
          className={ classnames(classes.button) }
          variant="outlined"
          onClick={ () => {
            window.open(item.invoice_pdf)
          }}
        >
          <PdfIcon />&nbsp;&nbsp;Download
        </Button>
      </>
    )
  }, [
    classes,
  ])

  const getCancelledSubscriptionActions = useCallback((item) => {
    return (
      <>
        <Button
          className={ classnames(classes.button) }
          variant="outlined"
          onClick={ () => {
            onSetViewCancelledSubscription(item.sub)
          }}
        >
          <InfoIcon />&nbsp;&nbsp;View
        </Button>
      </>
    )
  }, [
    classes,
  ])

  const subscriptionInfo = subscriptionUtils.getSubscriptionInfo(subscription)

  return (
    <div className={ classes.root }>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={7}>
          <Paper className={ classes.paper }>
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <Typography variant="h6" gutterBottom>
                  Websites
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <QuotaWarning />
              </Grid>
              <Grid item xs={4}>
                <div className={ classes.viewPlansButton }>
                  <Button
                    className={ classnames(classes.button) }
                    variant="outlined"
                    onClick={ () => setViewPlans(true) }
                  >
                    <VisibleIcon />&nbsp;&nbsp;View Plans
                  </Button>
                </div>
              </Grid>
              <Grid item xs={12}>
                <Table
                  data={ websiteTableData }
                  fields={ WEBSITE_FIELDS }
                  getActions={ getWebsiteActions }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {
          subscription && card && (
            <Grid item xs={12} sm={5}>
              <Paper className={ classes.paper }>
                <TitleValueGrid
                  title="Subscription"
                  data={ subscriptionUtils.getSubscriptionTable(subscription) }
                />
                <div className={ classes.sectionButton }>
                  {
                    subscription.cancel_at_period_end ? (
                      <div>
                        <Typography gutterBottom>
                          This subscription is cancelled and we will take no more payments.
                        </Typography>
                        <Typography gutterBottom>
                          You can still use all the features you have paid for up until { subscriptionInfo.periodEnd }
                        </Typography>
                        <div className={ classes.marginTop }>
                          <Button
                            className={ classes.button }
                            variant="outlined"
                            onClick={ () => onSetReactivateSubscriptionWindowOpen(true) }
                          >
                            <CloseIcon />&nbsp;&nbsp;Reactivate Subscription
                          </Button>
                        </div>  
                      </div>
                    ) : (
                      <Button
                        className={ classes.button }
                        variant="outlined"
                        onClick={ () => onSetCancelSubscriptionWindowOpen(true) }
                      >
                        <CloseIcon />&nbsp;&nbsp;Cancel Subscription
                      </Button>
                    )
                  }
                  
                </div>
              </Paper>
              <Paper className={ classes.paper }>
                <TitleValueGrid
                  title="Card"
                  data={[{
                    title: 'Type',
                    value: card.card.brand,
                  },{
                    title: 'Expires',
                    value: `${card.card.exp_month} / ${card.card.exp_year}`,
                  },{
                    title: 'Last 4 Digits',
                    value: card.card.last4,
                  }]}
                />
                <div className={ classes.sectionButton }>
                  <Button
                    className={ classes.button }
                    variant="outlined"
                    onClick={ onChangeCard }
                  >
                    <CardIcon />&nbsp;&nbsp;Change Card
                  </Button>
                </div>
              </Paper>
              <Paper className={ classes.paper }>
                <Grid container spacing={ 4 }>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Invoices
                    </Typography>
                  </Grid>
                </Grid>
                <Table
                  data={ invoiceTableData }
                  fields={ INVOICE_FIELDS }
                  getActions={ getInvoiceActions }
                />
              </Paper>
              {
                cancelledSubscriptions.length > 0 && (
                  <Paper className={ classes.paper }>
                    <Grid container spacing={ 4 }>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Previous Subscriptions
                        </Typography>
                      </Grid>
                    </Grid>
                    <Table
                      data={ cancelledSubscriptionTableData }
                      fields={ CANCALLED_SUBSCRIPTION_FIELDS }
                      getActions={ getCancelledSubscriptionActions }
                    />
                  </Paper>
                )
              }
            </Grid>
          )
        }
      </Grid>
      {
        changePlanWebsite && !changePlanInfoSummary && (
          <Window
            open
            withCancel
            cancelTitle="Close"
            size="md"
            onCancel={ () => onSetChangePlanWebsite(null) }
          >
            <div className={ classes.pricingToolbar }>
              <div className={ classes.pricingNote }>
                <Typography variant="caption">
                  Prices shown are per website
                </Typography>
              </div>
              <div className={ classes.currencies }>
                <Currency />
              </div>
            </div>
            
            <WebsitePayment
              website={ changePlanWebsite }
            />
          </Window>
        )
      }
      {
        changePlanWebsite && changePlanInfoSummary && (
          <ConfirmSubscriptionChangeWindow />
        )
      }
      {
        viewPlans && (
          <Window
            open
            withCancel
            cancelTitle="Close"
            size="md"
            onCancel={ () => setViewPlans(false) }
          >
            <div className={ classes.pricingToolbar }>
              <div className={ classes.pricingNote }>
                <Typography variant="caption">
                  Prices shown are per website
                </Typography>
              </div>
              <div className={ classes.currencies }>
                <Currency />
              </div>
            </div>
            <Products />
          </Window>
        )
      }
      {
        cancelSubscriptionWindowOpen && (
          <Window
            open
            withCancel
            title="Cancel Subscription"
            cancelTitle="Close"
            submitTitle="I am sure - cancel subscription"
            size="sm"
            onSubmit={ onCancelSubscription }
            onCancel={ () => onSetCancelSubscriptionWindowOpen(false) }
          >
            <Typography gutterBottom>
              Are you <strong>absolutely sure</strong> you want to cancel your subscription to nocode?
            </Typography>
            <Typography gutterBottom>
              We will immediately stop all payments but you have already paid until <br />
              <strong>{ subscriptionInfo.periodEnd }</strong> and can continue to use all features until then.
            </Typography>
            <Typography gutterBottom>
              After <strong>{ subscriptionInfo.periodEnd }</strong>, all websites will revert to the "free" plan.
            </Typography>
          </Window>
        )
      }
      {
        reactivateSubscriptionWindowOpen && (
          <Window
            open
            withCancel
            title="Reactivate Subscription"
            cancelTitle="Close"
            submitTitle="I am sure - reactivate subscription"
            size="sm"
            onSubmit={ onReactivateSubscription }
            onCancel={ () => onSetReactivateSubscriptionWindowOpen(false) }
          >
            <Typography gutterBottom>
              Are you <strong>absolutely sure</strong> you want to reactivate your subscription to nocode?
            </Typography>
            <Typography gutterBottom>
              This will continue your payments from <strong>{ subscriptionInfo.periodEnd }</strong> as normal.
            </Typography>
          </Window>
        )
      }
      {
        viewCancelledSubscription && (
          <Window
            open
            withCancel
            cancelTitle="Close"
            size="md"
            onCancel={ () => onSetViewCancelledSubscription(null) }
          >
            <TitleValueGrid
              title="Cancelled Subscription"
              data={ subscriptionUtils.getSubscriptionTable(viewCancelledSubscription) }
            />
            <Grid container spacing={ 4 } className={ classes.marginTop }>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Previous Invoices
                </Typography>
              </Grid>
            </Grid>
            <Table
              data={ cancelledSubscriptionInvoiceTableData }
              fields={ INVOICE_FIELDS }
              getActions={ getInvoiceActions }
            />
          </Window>
        )
      }
    </div>
  )
}

export default AccountPaymentsNocode